/**
 * Mock fixtures for the fulfillment / dunning / webhook-health admin surfaces
 * added in L67B. These back the mock client so the new pages render with zero
 * network in `VITE_ADMIN_API_MODE=mock`.
 *
 * The rows model a coherent slice of the Zone lifecycle across a few demo
 * memberships (checkout → ship → deliver → trial → active → past_due → retry
 * → recovered → return), so the lifecycle timeline / notice / webhook panels
 * tell one consistent story in mock mode. Shapes mirror the PROVISIONAL types
 * (see src/types/admin-{shipment,notice,webhook-health}.ts); the real backend
 * is the source of truth and may differ in exact keys — verify on staging.
 */

import type { AdminShipmentListItem } from "@/types/admin-shipment";
import type {
  AdminMembershipNoticeRow,
  AdminMembershipNoticeSummary,
} from "@/types/admin-notice";
import type {
  AdminOutboxHealth,
  AdminEasypostWebhookHealth,
  AdminWebhookEventRow,
} from "@/types/admin-webhook-health";

export const MOCK_SHIPMENTS_LIST: AdminShipmentListItem[] = [
  {
    membershipId: "m_1001",
    fulfillmentOrderId: "fo_1001",
    shipmentId: "shp_1001",
    shipmentStatus: "delivered",
    carrier: "USPS",
    trackingNumber: "9400111899223197428490",
    trackingStatus: "delivered",
    shippedAt: "2026-06-08T15:04:00Z",
    deliveredAt: "2026-06-10T18:22:00Z",
    membershipState: "trial_active",
  },
  {
    membershipId: "m_1002",
    fulfillmentOrderId: "fo_1002",
    shipmentId: "shp_1002",
    shipmentStatus: "in_transit",
    carrier: "UPS",
    trackingNumber: "1Z999AA10123456784",
    trackingStatus: "in_transit",
    shippedAt: "2026-06-14T09:30:00Z",
    deliveredAt: null,
    membershipState: "pending_delivery",
  },
  {
    membershipId: "m_1003",
    fulfillmentOrderId: "fo_1003",
    shipmentId: null,
    shipmentStatus: "pending",
    carrier: null,
    trackingNumber: null,
    trackingStatus: null,
    shippedAt: null,
    deliveredAt: null,
    membershipState: "pending_shipment",
  },
  {
    membershipId: "m_1004",
    fulfillmentOrderId: "fo_1004",
    shipmentId: "shp_1004",
    shipmentStatus: "delivered",
    carrier: "FedEx",
    trackingNumber: "612345678901",
    trackingStatus: "delivered",
    shippedAt: "2026-05-02T12:00:00Z",
    deliveredAt: "2026-05-04T16:40:00Z",
    membershipState: "active",
  },
  {
    membershipId: "m_1005",
    fulfillmentOrderId: "fo_1005",
    shipmentId: "shp_1005",
    shipmentStatus: "delivered",
    carrier: "USPS",
    trackingNumber: "9400111899223197400011",
    trackingStatus: "delivered",
    shippedAt: "2026-04-19T14:10:00Z",
    deliveredAt: "2026-04-21T11:05:00Z",
    membershipState: "past_due",
  },
  {
    membershipId: "m_1006",
    fulfillmentOrderId: "fo_1006",
    shipmentId: "shp_1006",
    shipmentStatus: "returned",
    carrier: "USPS",
    trackingNumber: "9400111899223197499999",
    trackingStatus: "returned_to_sender",
    shippedAt: "2026-03-11T10:00:00Z",
    deliveredAt: "2026-03-13T13:30:00Z",
    membershipState: "returned",
  },
];

export const MOCK_MEMBERSHIP_NOTICES_FAILED: AdminMembershipNoticeRow[] = [
  {
    noticeId: "9001",
    membershipId: "m_1005",
    userId: "u_005",
    noticeType: "payment_failed",
    channel: "email",
    status: "failed",
    recipientMasked: "c••@wear.zone",
    createdAt: "2026-06-12T08:00:00Z",
    sentAt: null,
    failedReason: "smtp_temporary_failure",
    retryScheduledAt: null,
  },
  {
    noticeId: "9002",
    membershipId: "m_1007",
    userId: "u_007",
    noticeType: "exhausted",
    channel: "email",
    status: "failed",
    recipientMasked: "d••@wear.zone",
    createdAt: "2026-06-09T07:30:00Z",
    sentAt: null,
    failedReason: "bounced_recipient",
    retryScheduledAt: null,
  },
];

export const MOCK_MEMBERSHIP_NOTICES_DUE_PENDING: AdminMembershipNoticeRow[] = [
  {
    noticeId: "9100",
    membershipId: "m_1005",
    userId: "u_005",
    noticeType: "retry_scheduled",
    channel: "email",
    status: "pending",
    recipientMasked: "c••@wear.zone",
    createdAt: "2026-06-15T08:00:00Z",
    sentAt: null,
    failedReason: null,
    retryScheduledAt: "2026-06-18T08:00:00Z",
  },
  {
    noticeId: "9101",
    membershipId: "m_1008",
    userId: "u_008",
    noticeType: "payment_failed",
    channel: "email",
    status: "pending",
    recipientMasked: "e••@wear.zone",
    createdAt: "2026-06-16T06:00:00Z",
    sentAt: null,
    failedReason: null,
    retryScheduledAt: null,
  },
];

export const MOCK_MEMBERSHIP_NOTICES_SUMMARY: AdminMembershipNoticeSummary = {
  total: 42,
  pending: MOCK_MEMBERSHIP_NOTICES_DUE_PENDING.length,
  sent: 38,
  failed: MOCK_MEMBERSHIP_NOTICES_FAILED.length,
  duePending: MOCK_MEMBERSHIP_NOTICES_DUE_PENDING.length,
  byType: {
    payment_failed: 16,
    retry_scheduled: 14,
    recovered: 9,
    exhausted: 3,
  },
};

export const MOCK_OUTBOX_HEALTH: AdminOutboxHealth = {
  pending: 2,
  failed: 0,
  deadLettered: 0,
  oldestPendingAgeSeconds: 34,
  publishedLast24h: 1287,
  healthy: true,
};

export const MOCK_EASYPOST_WEBHOOK_HEALTH: AdminEasypostWebhookHealth = {
  // Ingestion is 503-disabled by default in non-prod (L66 G7) — model that.
  enabled: false,
  received: 0,
  processed: 0,
  failed: 0,
  stale: 0,
  healthy: false,
};

export const MOCK_EASYPOST_WEBHOOK_EVENTS: AdminWebhookEventRow[] = [
  {
    eventId: "evt_ep_1001",
    source: "easypost",
    eventType: "tracker.updated",
    status: "processed",
    receivedAt: "2026-06-14T09:31:00Z",
    processedAt: "2026-06-14T09:31:02Z",
    acknowledgedAt: "2026-06-14T09:31:02Z",
    error: null,
  },
  {
    eventId: "evt_ep_1002",
    source: "easypost",
    eventType: "tracker.updated",
    status: "failed",
    receivedAt: "2026-06-13T22:10:00Z",
    processedAt: null,
    acknowledgedAt: null,
    error: "signature_verification_failed",
  },
];
